// Unified WebSocket adapter that works with both Socket.io (local) and native WebSocket (Cloudflare)
import { io, Socket } from 'socket.io-client';

export interface WebSocketAdapter {
  on(event: string, callback: (data: any) => void): void;
  emit(event: string, data: any): void;
  off(event: string, callback?: (data: any) => void): void;
  id: string | null;
  connected: boolean;
  disconnect(): void;
  close(): void;
}

class SocketIOAdapter implements WebSocketAdapter {
  private socket: Socket;

  constructor(url: string) {
    this.socket = io(url);
  }

  on(event: string, callback: (data: any) => void): void {
    this.socket.on(event, callback);
  }

  emit(event: string, data: any): void {
    this.socket.emit(event, data);
  }

  off(event: string, callback?: (data: any) => void): void {
    if (callback) {
      this.socket.off(event, callback);
    } else {
      this.socket.off(event);
    }
  }

  get id(): string | null {
    return this.socket.id || null;
  }

  get connected(): boolean {
    return this.socket.connected;
  }

  disconnect(): void {
    this.socket.disconnect();
  }

  close(): void {
    this.disconnect();
  }
}

class NativeWebSocketAdapter implements WebSocketAdapter {
  private ws: WebSocket | null = null;
  private eventHandlers: Map<string, Set<(data: any) => void>> = new Map();
  private _id: string | null = null;
  private _connected: boolean = false;
  private url: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private _pendingConnect: boolean = false;

  constructor(url: string) {
    this.url = url;
    this.connect();
  }

  private connect(): void {
    try {
      // Convert http/https to ws/wss
      const wsUrl = this.url.replace(/^http/, 'ws');
      const ws = new WebSocket(`${wsUrl}?transport=websocket`);
      
      ws.onopen = () => {
        console.log('Native WebSocket connected - waiting for backend socket ID...');
        this._connected = true;
        this.reconnectAttempts = 0;
        this._pendingConnect = true;
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type && message.data !== undefined) {
            // If this is the connect message from backend, use its socket ID
            if (message.type === 'connect' && message.data?.id && this._pendingConnect) {
              console.log('Received socket ID from backend:', message.data.id);
              this._id = message.data.id;
              this._pendingConnect = false;
              this.triggerEvent('connect', { id: this._id });
              return;
            }
            // Handle structured message: { type: 'event', data: {...} }
            this.triggerEvent(message.type, message.data);
          } else {
            console.warn('Unexpected message format:', message);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error, event.data);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.triggerEvent('error', error);
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        this._connected = false;
        this.triggerEvent('disconnect', { reason: event.reason || 'Connection closed' });
        
        // Attempt to reconnect
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          setTimeout(() => this.connect(), this.reconnectDelay * this.reconnectAttempts);
        }
      };

      this.ws = ws;
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      this._connected = false;
    }
  }

  private triggerEvent(event: string, data: any): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  on(event: string, callback: (data: any) => void): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(callback);
  }

  emit(event: string, data: any): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket is not open. Cannot emit:', event);
      return;
    }

    const message = {
      type: event,
      data: data
    };

    try {
      this.ws.send(JSON.stringify(message));
    } catch (error) {
      console.error('Error sending WebSocket message:', error);
    }
  }

  off(event: string, callback?: (data: any) => void): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      if (callback) {
        handlers.delete(callback);
      } else {
        handlers.clear();
      }
    }
  }

  get id(): string | null {
    return this._id;
  }

  get connected(): boolean {
    return this._connected && this.ws?.readyState === WebSocket.OPEN;
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this._connected = false;
    this.eventHandlers.clear();
  }

  close(): void {
    this.disconnect();
  }
}

export function createWebSocketAdapter(url: string): WebSocketAdapter {
  // Use native WebSocket for Cloudflare Workers, Socket.io for localhost
  const isCloudflare = url.includes('workers.dev') || url.includes('pages.dev');
  
  if (isCloudflare) {
    console.log('Using native WebSocket adapter for Cloudflare');
    return new NativeWebSocketAdapter(url);
  } else {
    console.log('Using Socket.io adapter for local development');
    return new SocketIOAdapter(url);
  }
}
